import { IconClose } from "@/assets/icons/IconClose";
import { IconCopy } from "@/assets/icons/IconCopy";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Copyable } from "@/components/elements/Copyable";
import { FormField } from "@/components/elements/FormField";
import { AvatarUploadDropzone } from "@/features/talent/components/talent-form/AvatarUploadDropzone";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { background, button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { getFileNameFromPath } from "@/utils/file";
import { truncateFileNameMiddle, truncateWithEllipsis } from "@/utils/string";
import { Box, Divider, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

export const AvatarUploadForm = () => {
  const { id } = useParams();
  const { control, setValue } = useFormContext<TTalentForm>();

  const [externalLinkAvatar, setExternalLinkAvatar] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [avatarObjectUrl, setAvatarObjectUrl] = useState<string | null>(null);
  const avatarObjectUrlRef = useRef<string | null>(null);

  const avatarFileWatchField = useWatch({
    control,
    name: "avatarFile",
  });

  const avatarFilePathWatchField = useWatch({
    control,
    name: "avatarFilePath",
  });

  const handleGetLinkAvatar = async () => {
    // if (!id) return "";
    // const rs = await fetchTalentInfoApi(id);
    // const link = createUrlWithParams(`${window.location.origin}/talent/${rs?.data.id}/upload-avatar`, {
    //   token: rs?.data.secretToken,
    // });
    // setExternalLinkAvatar(link);
  };

  const handleDeleteAvatar = () => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    setAvatarObjectUrl(null);
    setValue("avatarFile", null);
    setValue("avatarFilePath", "");
  };

  const hasAvatar = avatarFileWatchField || avatarFilePathWatchField;

  const displayFileName = avatarFileWatchField?.name || getFileNameFromPath(avatarFilePathWatchField ?? "") || "";

  useEffect(() => {
    const previousObjectUrl = avatarObjectUrlRef.current;

    if (avatarFileWatchField && avatarFileWatchField instanceof File && avatarFileWatchField.size > 0) {
      const newObjectUrl = URL.createObjectURL(avatarFileWatchField);
      avatarObjectUrlRef.current = newObjectUrl;
      setAvatarObjectUrl(newObjectUrl);
    } else {
      avatarObjectUrlRef.current = null;
      setAvatarObjectUrl(null);
    }

    return () => {
      if (previousObjectUrl) {
        URL.revokeObjectURL(previousObjectUrl);
      }
    };
  }, [avatarFileWatchField]);

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      <Grid item xs={8} sx={{ height: "auto" }}>
        <FormField
          control={control}
          name="avatarFile"
          sx={{
            height: "100%",
            border: `1px dashed ${isDragging ? button.primary : divider.middle}`,
            width: "100%",
            transition: "border-color 0.2s ease",
            borderRadius: rounded.sm,
          }}
        >
          <AvatarUploadDropzone onDragStateChange={setIsDragging} />
        </FormField>
        {id && (
          <Stack border={`1px solid ${divider.middle}`} padding={3} gap={2} borderRadius={rounded.sm} mt={3}>
            <Typography variant="sub16Semi">画像アップロードURLの発行</Typography>
            <Typography variant="cap12">特定技能外国人の方に直接画像アップロードを依頼する場合は、こちらからリンクURLを発行してください。</Typography>
            <Stack flexDirection="row" gap={2} alignItems="center">
              <Button variant="outline" size="sm" onClick={() => handleGetLinkAvatar()} disabled={!id}>
                URLを発行
              </Button>
              <Typography
                sx={{ minHeight: "35px" }}
                variant="body14"
                flexGrow={1}
                component="p"
                border={`1px solid ${divider.middle}`}
                borderRadius={rounded.xs}
                paddingX={1}
                paddingY="6px"
              >
                {truncateWithEllipsis(externalLinkAvatar, 57)}
              </Typography>
              <Copyable color={externalLinkAvatar ? button.primary : button.disable} text={externalLinkAvatar}>
                <IconCopy width={19} height={22} cursor="pointer" />
              </Copyable>
            </Stack>
          </Stack>
        )}
      </Grid>
      <Grid item xs={4}>
        <Box display="flex" justifyContent="center" width="100%">
          <Avatar
            filePath={avatarFileWatchField?.filePath || avatarFilePathWatchField}
            imageSrc={avatarObjectUrl || undefined}
            width={120}
            height={160}
          />
        </Box>
        <Stack mt={2} gap={0.5}>
          <Typography variant="body14" color={text.secondary}>
            ファイル名
          </Typography>
          <Divider />
          {hasAvatar ? (
            <Tooltip
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: background.white,
                    color: text.primary,
                    boxShadow: "0px 2px 8px 0px #00000040",
                  },
                },
                arrow: {
                  sx: {
                    color: background.white,
                  },
                },
              }}
              title={
                <Typography
                  variant="cap12"
                  sx={{
                    backgroundColor: background.white,
                    color: text.primary,
                  }}
                >
                  {displayFileName}
                </Typography>
              }
            >
              <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body14"
                  color={text.secondary}
                  sx={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {truncateFileNameMiddle(displayFileName, 30)}
                </Typography>

                <Box
                  onClick={handleDeleteAvatar}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: text.secondary,
                    "&:hover": {
                      color: text.primary,
                    },
                  }}
                >
                  <IconClose width={24} height={24} />
                </Box>
              </Stack>
            </Tooltip>
          ) : null}
        </Stack>
      </Grid>
    </Grid>
  );
};
